import type { Editor } from '@tiptap/core';

/**
 * Package-internal apply marks (`/react`-private; task 6.1, design D5).
 *
 * The controller's origin classifier must never emit a curated content
 * callback for a package-performed apply — hydration, controlled prop sync,
 * forced apply, empty reconciliation, streaming frames, canonical finalize.
 * Almost all of those are already invisible (`emitUpdate: false`), but the
 * characterized empty-clear deliberately fires `update` so the host autosave
 * keeps its frozen single-save trace (ARCFE-863). These marks are how the two
 * `/react` primitives coordinate without observable coupling: the sync hook
 * marks the editor for the synchronous duration of each apply, and the
 * controller reads the mark inside the synchronously-dispatched `update`.
 *
 * WeakSets keyed on the editor — the same module-privacy reasoning as the
 * sync hook's preview-taint set: writable only by the paths that own the
 * lifecycle, leak-free across editor destruction, and invisible to hosts.
 */
const controlledApplies = new WeakSet<Editor>();
const imperativeApplies = new WeakSet<Editor>();

/** Wraps a package-performed apply; `update` fired inside is package-origin. */
export const runControlledApply = <T>(editor: Editor, apply: () => T): T => {
  controlledApplies.add(editor);
  try {
    return apply();
  } finally {
    controlledApplies.delete(editor);
  }
};

/** Wraps a curated-handle mutation; `update` fired inside is `imperative`. */
export const runImperativeApply = <T>(editor: Editor, apply: () => T): T => {
  imperativeApplies.add(editor);
  try {
    return apply();
  } finally {
    imperativeApplies.delete(editor);
  }
};

export const isControlledApply = (editor: Editor): boolean =>
  controlledApplies.has(editor);

export const isImperativeApply = (editor: Editor): boolean =>
  imperativeApplies.has(editor);
